import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Package, Sparkles, Zap, Leaf, Heart, Coffee, Cloud, Gift, Scissors, Cog, Globe, Users, Wine, Server, ShoppingBag, Star, CircuitBoard, TreePine, BookOpen, UtensilsCrossed, Wifi, ArrowLeft, ArrowRight, Palette, Anchor, Building2, Newspaper, Store, GraduationCap, Plug, BarChart3, MapPin, Monitor, Layout, Squirrel, Shield, Recycle } from 'lucide-react';
function AnimatedBackground({
  color,
  icon: Icon,
  secondaryIcon: SecondaryIcon,
  tertiaryIcon: TertiaryIcon,
  accentColor
}: {
  color: string;
  icon: React.ElementType;
  secondaryIcon: React.ElementType;
  tertiaryIcon: React.ElementType;
  accentColor: string;
}) {
  return <div className={`w-full h-full ${color} transition-transform duration-500 group-hover:scale-110 relative overflow-hidden`} data-id="element-147">
      <div className="absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
      backgroundSize: '24px 24px',
      color: 'white'
    }} data-id="element-148" />
      <motion.div className="absolute inset-0 flex items-center justify-center" animate={{
      y: [0, -8, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-149">
        <Icon size={56} className={`${accentColor} opacity-20`} strokeWidth={1} data-id="element-150" />
      </motion.div>
      <motion.div className="absolute top-6 right-6" animate={{
      rotate: [0, 360],
      scale: [1, 1.1, 1]
    }} transition={{
      rotate: {
        duration: 20,
        repeat: Infinity,
        ease: 'linear'
      },
      scale: {
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }} data-id="element-151">
        <SecondaryIcon size={20} className={`${accentColor} opacity-15`} strokeWidth={1.5} data-id="element-152" />
      </motion.div>
      <motion.div className="absolute bottom-8 left-8" animate={{
      scale: [1, 1.2, 1],
      opacity: [0.1, 0.2, 0.1]
    }} transition={{
      duration: 3.5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.5
    }} data-id="element-153">
        <TertiaryIcon size={22} className={`${accentColor}`} strokeWidth={1.5} data-id="element-154" />
      </motion.div>
      <motion.div className={`absolute top-1/4 left-1/4 w-2 h-2 rounded-full ${accentColor.replace('text-', 'bg-')} opacity-20`} animate={{
      y: [0, -12, 0],
      x: [0, 6, 0]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} data-id="element-155" />
      <motion.div className={`absolute bottom-1/4 right-1/4 w-10 h-10 rounded-full border ${accentColor.replace('text-', 'border-')} opacity-10`} animate={{
      scale: [1, 1.3, 1],
      opacity: [0.1, 0.05, 0.1]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 2
    }} data-id="element-156" />
      <motion.div className={`absolute top-12 left-12 w-8 h-[1px] ${accentColor.replace('text-', 'bg-')} opacity-15`} animate={{
      scaleX: [1, 1.5, 1],
      opacity: [0.15, 0.08, 0.15]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.8
    }} data-id="element-157" />
    </div>;
}
const ALL_FILTER = 'All';
export function PortfolioSection() {
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER);
  const projects = [{
    id: 1,
    title: 'JYSK UK',
    category: 'Drupal / E-commerce',
    tags: ['Development', 'E-commerce'],
    website: 'https://jysk.co.uk/',
    color: 'bg-amber-900',
    icon: ShoppingBag,
    secondaryIcon: Package,
    tertiaryIcon: Store,
    accentColor: 'text-amber-300'
  }, {
    id: 2,
    title: 'Seacat by Rossinavi',
    category: 'React / Luxury Marine',
    tags: ['Development'],
    website: 'https://seacat.rossinavi.it/',
    color: 'bg-sky-900',
    icon: Anchor,
    secondaryIcon: Globe,
    tertiaryIcon: Sparkles,
    accentColor: 'text-sky-300'
  }, {
    id: 3,
    title: 'TransfrInc',
    category: 'Design / Corporate',
    tags: ['Design', 'Corporate'],
    website: 'https://transfrinc.com/',
    color: 'bg-slate-800',
    icon: Palette,
    secondaryIcon: Layout,
    tertiaryIcon: Monitor,
    accentColor: 'text-slate-300'
  }, {
    id: 6,
    title: 'MyCPACoach',
    category: 'Design / Education',
    tags: ['Design'],
    website: 'https://mycpacoach.com/',
    color: 'bg-indigo-900',
    icon: GraduationCap,
    secondaryIcon: BookOpen,
    tertiaryIcon: Star,
    accentColor: 'text-indigo-300'
  }, {
    id: 8,
    title: 'BENS',
    category: 'Custom Plugin / Non-Profit',
    tags: ['Development', 'Non-Profit'],
    website: 'https://bens.org/',
    color: 'bg-emerald-900',
    icon: Heart,
    secondaryIcon: Plug,
    tertiaryIcon: Users,
    accentColor: 'text-emerald-300'
  }, {
    id: 9,
    title: 'SABCap',
    category: 'Corporate / Finance',
    tags: ['Corporate'],
    website: 'https://sabcap.com',
    color: 'bg-zinc-800',
    icon: Building2,
    secondaryIcon: BarChart3,
    tertiaryIcon: Globe,
    accentColor: 'text-zinc-300'
  }, {
    id: 10,
    title: 'MyCalgary',
    category: 'Community / Portal',
    tags: ['Portal', 'Development'],
    website: 'https://mycalgary.com',
    color: 'bg-blue-900',
    icon: MapPin,
    secondaryIcon: Globe,
    tertiaryIcon: Users,
    accentColor: 'text-blue-300'
  }, {
    id: 13,
    title: 'The Human Squirrel',
    category: 'Creative / Web Design',
    tags: ['Design'],
    website: 'https://thehumansquirrel.com/',
    color: 'bg-amber-800',
    icon: Squirrel,
    secondaryIcon: Coffee,
    tertiaryIcon: Palette,
    accentColor: 'text-amber-200'
  }, {
    id: 14,
    title: 'WDCACDST',
    category: 'Non-Profit / Organization',
    tags: ['Non-Profit', 'Development'],
    website: 'https://wdcacdst.org/',
    color: 'bg-cyan-900',
    icon: Users,
    secondaryIcon: Globe,
    tertiaryIcon: BookOpen,
    accentColor: 'text-cyan-300'
  }, {
    id: 15,
    title: 'EcoMinim',
    category: 'E-commerce / Sustainable',
    tags: ['E-commerce', 'Design'],
    website: 'https://ecominim.com/',
    color: 'bg-green-900',
    icon: Leaf,
    secondaryIcon: Recycle,
    tertiaryIcon: TreePine,
    accentColor: 'text-green-300'
  }, {
    id: 16,
    title: 'Little Brand Box',
    category: 'E-commerce / Subscription Retail',
    tags: ['E-commerce'],
    website: 'https://littlebrandbox.com',
    color: 'bg-amber-900',
    icon: Package,
    secondaryIcon: Gift,
    tertiaryIcon: ShoppingBag,
    accentColor: 'text-amber-300'
  }, {
    id: 17,
    title: 'Hair Health',
    category: 'E-commerce / Health & Beauty',
    tags: ['E-commerce', 'Design'],
    website: 'https://hairhealth.co.za',
    color: 'bg-rose-900',
    icon: Sparkles,
    secondaryIcon: Scissors,
    tertiaryIcon: Star,
    accentColor: 'text-rose-300'
  }, {
    id: 18,
    title: 'Africa Switchgear',
    category: 'Industrial / Manufacturing',
    tags: ['Corporate', 'Development'],
    website: 'https://africaswitchgear.co.za',
    color: 'bg-slate-800',
    icon: Zap,
    secondaryIcon: Cog,
    tertiaryIcon: CircuitBoard,
    accentColor: 'text-yellow-300'
  }, {
    id: 19,
    title: 'TerraHub Marketplace',
    category: 'E-commerce / Sustainable Marketplace',
    tags: ['E-commerce', 'Development'],
    website: 'https://terrahubmarketplace.co.za',
    color: 'bg-emerald-900',
    icon: Leaf,
    secondaryIcon: Globe,
    tertiaryIcon: TreePine,
    accentColor: 'text-emerald-300'
  }, {
    id: 20,
    title: 'Global Parenting Initiative',
    category: 'Non-Profit / Education',
    tags: ['Non-Profit', 'Design'],
    website: 'https://globalparenting.org',
    color: 'bg-blue-900',
    icon: Heart,
    secondaryIcon: Users,
    tertiaryIcon: BookOpen,
    accentColor: 'text-blue-300'
  }, {
    id: 22,
    title: 'Cloudit',
    category: 'Technology / IT Services',
    tags: ['Corporate', 'Development'],
    website: 'https://cloudit.co',
    color: 'bg-purple-900',
    icon: Cloud,
    secondaryIcon: Server,
    tertiaryIcon: Wifi,
    accentColor: 'text-purple-300'
  }, {
    id: 23,
    title: 'Maiden Cricket',
    category: 'Shopify / E-commerce',
    tags: ['Shopify', 'E-commerce'],
    website: 'https://maiden-cricket.co.uk/',
    color: 'bg-green-900',
    icon: ShoppingBag,
    secondaryIcon: Store,
    tertiaryIcon: Star,
    accentColor: 'text-green-300'
  }];
  // Derive unique filter categories dynamically
  const filterCategories = useMemo(() => {
    const tagSet = new Set<string>();
    projects.forEach(p => p.tags.forEach(t => tagSet.add(t)));
    return [ALL_FILTER, ...Array.from(tagSet).sort()];
  }, []);
  const filteredProjects = useMemo(() => {
    if (activeFilter === ALL_FILTER) return projects;
    return projects.filter(p => p.tags.includes(activeFilter));
  }, [activeFilter]);
  return <section id="portfolio" className="py-24 bg-dark-lighter overflow-hidden" data-id="element-158">
      <div className="max-w-[1320px] mx-auto px-4 relative" data-id="element-159">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row justify-between items-end mb-12 relative z-10" data-id="element-160">
          {/* Watermark */}
          <div className="absolute -top-20 -left-10 text-[120px] font-bold text-white/[0.02] font-syne whitespace-nowrap pointer-events-none select-none hidden lg:block" data-id="element-161">
            OUR PROJECTS
          </div>

          {/* Left Content */}
          <div className="mb-8 lg:mb-0 relative" data-id="element-162">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-gold font-inter font-bold tracking-wider text-sm uppercase mb-2 block" data-id="element-163">
              Projects
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-5xl md:text-6xl font-bold text-white font-syne uppercase leading-tight" data-id="element-164">
              Latest
            </motion.h2>
          </div>

          {/* Right Content */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="max-w-md pl-6 border-l-2 border-gold" data-id="element-165">
            <p className="text-white/60 font-inter text-lg leading-relaxed" data-id="element-166">
              Explore my latest work and see how we bring creative visions to
              life through design and technology.
            </p>
          </motion.div>
        </div>

        {/* Filter Tabs */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="flex flex-wrap gap-3 mb-12 relative" data-id="element-167">
          {filterCategories.map(category => <button key={category} onClick={() => setActiveFilter(category)} className="relative px-6 py-2.5 font-inter text-sm font-medium rounded-full transition-colors duration-300" data-id="element-168">
              {activeFilter === category && <motion.div layoutId="activeFilterBg" className="absolute inset-0 bg-gold rounded-full" transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} data-id="element-169" />}
              <span className={`relative z-10 transition-colors duration-200 ${activeFilter === category ? 'text-white' : 'text-white/50 hover:text-white/80'}`} data-id="element-170">
                {category}
              </span>
            </button>)}
        </motion.div>

        {/* Projects Grid */}
        <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-171">
          <AnimatePresence mode="popLayout" data-id="element-172">
            {filteredProjects.map(project => <motion.a key={project.id} href={project.website} target="_blank" rel="noopener noreferrer" layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="group relative aspect-square rounded-lg overflow-hidden cursor-pointer block bg-dark" data-id="element-173">
                {/* Animated Icon Background */}
                <AnimatedBackground color={project.color} icon={project.icon} secondaryIcon={project.secondaryIcon} tertiaryIcon={project.tertiaryIcon} accentColor={project.accentColor} data-id="element-174" />

                {/* Overlay */}
                <div className="absolute inset-0 bg-dark/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-center items-center p-3 text-center border border-gold/0 group-hover:border-gold/20 m-2 rounded-md z-10" data-id="element-175">
                  <span className="text-gold font-inter text-xs font-medium mb-1 uppercase tracking-wider translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-75" data-id="element-176">
                    {project.category}
                  </span>
                  <h3 className="text-base font-bold text-white font-syne mb-1 translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-100" data-id="element-177">
                    {project.title}
                  </h3>
                  <span className="text-white/50 font-inter text-[10px] mb-3 translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-100" data-id="element-178">
                    {project.website.replace('https://', '').replace(/\/$/, '')}
                  </span>
                  <div className="w-9 h-9 rounded-full bg-gold flex items-center justify-center text-black group-hover:bg-white transition-colors translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-150" data-id="element-179">
                    <ArrowUpRight size={18} data-id="element-180" />
                  </div>
                </div>
              </motion.a>)}
          </AnimatePresence>
        </motion.div>

        {/* Results count */}
        <motion.p layout className="text-white/30 font-inter text-sm mt-8 text-center" data-id="element-181">
          Showing {filteredProjects.length} of {projects.length} projects
        </motion.p>
      </div>
    </section>;
}